/**
 * Dashboard Page
 * Overview of bookings and quick stats
 */

import { useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import QueryError from "../components/QueryError";
import ConfirmDialog from "../components/ConfirmDialog";
import PartnerPageHeader from "../components/PartnerPageHeader";
import { canCancelBooking } from "../utils/booking-permissions";
import { ApiError } from "../services/api-error";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "../stores/auth-store";
import {
  getBookings,
  cancelBooking,
  updateBookingStatus,
  createFortnoxInvoice,
  cancelFortnoxInvoice,
} from "../services/api";
import { BookingCard, InvoiceModal, EditBookingModal, BookingDetailsModal } from "../components";
import { format, startOfMonth, endOfMonth, addMonths } from "date-fns";
import type { Booking } from "@common/types";
import { hasTripStartPassed } from "../utils/booking";
import { readListState, sortByDate, writeListState, type BookingSort } from "../utils/booking-list";

type StatusFilter =
  | "all"
  | "active"
  | "pending"
  | "confirmed"
  | "toInvoice"
  | "invoiced"
  | "completed"
  | "cancelled";

export default function DashboardPage() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const isAdmin = user?.role === "admin";
  // filter, search, sort and page are kept in the address: reload and the back button keep the place
  const [params, setParams] = useSearchParams();
  const list = readListState(params);
  const statusFilter: StatusFilter = list.filter;
  const searchQuery = list.query;
  const sortMode: BookingSort = list.sort;
  const currentPage = list.page;
  const update = (patch: Parameters<typeof writeListState>[1]) => setParams((prev) => writeListState(prev, patch), { replace: true });
  const setStatusFilter = (filter: StatusFilter) => update({ filter });
  const setSearchQuery = (query: string) => update({ query });
  const setCurrentPage = (next: number | ((page: number) => number)) => update({ page: typeof next === "function" ? next(currentPage) : next });
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);
  const [viewingBooking, setViewingBooking] = useState<Booking | null>(null);
  const BOOKINGS_PER_PAGE = 20;
  // Invoice modal state
  const [invoicingBooking, setInvoicingBooking] = useState<Booking | null>(
    null,
  );
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  // cancelling one booking, and confirming several pending ones at once
  const [cancelling, setCancelling] = useState<Booking | null>(null);
  const [compactBookings, setCompactBookings] = useState(true);
  const [selected, setSelected] = useState<string[]>([]);
  const [bulkAsk, setBulkAsk] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState<{ done: number; total: number; failed: string[] } | null>(null);

  // Date range adapts to the selected filter:
  // - completed: 3 months back → end of current month (needs invoicing, past-focused)
  // - pending/confirmed: start of current month → 11 months ahead (upcoming, future-focused)
  // - everything else: 3 months back → 11 months ahead (both directions)
  const today = new Date();
  const isPastFocused = statusFilter === "completed";
  const isFutureFocused =
    statusFilter === "pending" || statusFilter === "confirmed";
  const rangeStart = format(
    startOfMonth(addMonths(today, isFutureFocused ? 0 : -3)),
    "yyyy-MM-dd",
  );
  const rangeEnd = format(
    endOfMonth(addMonths(today, isPastFocused ? 0 : 11)),
    "yyyy-MM-dd",
  );

  const { data: bookingsData, isLoading: bookingsLoading, isError: bookingsError, refetch: refetchBookings } = useQuery({
    queryKey: ["bookings", rangeStart, rangeEnd],
    queryFn: () => getBookings({ startDate: rangeStart, endDate: rangeEnd }),
  });


  // Cancel booking mutation
  const cancelMutation = useMutation({
    mutationFn: ({ id, date }: { id: string; date: string }) =>
      cancelBooking(id, date),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({ queryKey: ["booking-history"] });
    },
  });

  // Update status mutation (for admin)
  const updateStatusMutation = useMutation({
    mutationFn: ({
      id,
      date,
      status,
      invoiceNumber,
    }: {
      id: string;
      date: string;
      status: string;
      invoiceNumber?: string;
    }) => updateBookingStatus(id, date, status, undefined, invoiceNumber),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({ queryKey: ["booking-history"] });
    },
  });

  const handleCancelBooking = (booking: Booking) => {
    cancelMutation.reset();
    setCancelling(booking);
  };

  // Confirm the chosen pending bookings one by one, the same way as confirming one; say which ones failed
  const runBulkConfirm = async () => {
    setBulkBusy(true);
    const chosen = bookings.filter((b) => selected.includes(b.id) && b.status === "pending");
    const failed: string[] = [];
    for (const b of chosen) {
      try {
        await updateBookingStatus(b.id, b.date, "confirmed");
      } catch {
        failed.push(b.customer?.name || b.confirmationCode);
      }
    }
    setBulkResult({ done: chosen.length - failed.length, total: chosen.length, failed });
    setSelected([]);
    setBulkAsk(false);
    setBulkBusy(false);
    void queryClient.invalidateQueries({ queryKey: ["bookings"] });
    void queryClient.invalidateQueries({ queryKey: ["booking-history"] });
  };

  const handleUpdateStatus = (
    bookingId: string,
    date: string,
    status: string,
    invoiceNumber?: string,
  ) => {
    updateStatusMutation.mutate({ id: bookingId, date, status, invoiceNumber });
  };

  // Invoice creation mutation
  const createInvoiceMutation = useMutation({
    mutationFn: async ({
      bookingId,
      bookingDate,
      invoiceDate,
    }: {
      bookingId: string;
      bookingDate: string;
      invoiceDate: string;
    }) => {
      const result = await createFortnoxInvoice({
        bookingId,
        bookingDate,
        invoiceDate,
      });
      if (!result.success) {
        throw new Error(result.error || t("partnerPages.dashboard.invoiceCreateFailed"));
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({ queryKey: ["booking-history"] });
      setInvoicingBooking(null);
      setInvoiceError(null);
    },
    onError: (error: Error) => {
      setInvoiceError(error.message);
    },
  });

  const handleCreateInvoice = async (invoiceDate: string) => {
    if (!invoicingBooking) return;
    setInvoiceError(null);
    await createInvoiceMutation.mutateAsync({
      bookingId: invoicingBooking.id,
      bookingDate: invoicingBooking.date,
      invoiceDate,
    });
  };

  // Cancel invoice mutation
  const cancelInvoiceMutation = useMutation({
    mutationFn: async ({
      documentNumber,
      bookingId,
      bookingDate,
    }: {
      documentNumber: string;
      bookingId: string;
      bookingDate: string;
    }) => {
      const result = await cancelFortnoxInvoice(
        documentNumber,
        bookingId,
        bookingDate,
      );
      if (!result.success) {
        throw new Error(result.error || t("partnerPages.dashboard.invoiceCancelFailed", { message: t("partnerPages.dashboard.unknownError") }));
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({ queryKey: ["booking-history"] });
    },
  });

  const handleCancelInvoice = async (booking: Booking) => {
    if (!booking.fortnoxInvoiceNumber) {
      alert(t("partnerPages.dashboard.invoiceMissing"));
      return;
    }
    if (booking.stripePaymentStatus) {
      alert(
        t("partnerPages.dashboard.invoicePaymentLink"),
      );
      return;
    }
    if (
      !window.confirm(
        t("partnerPages.dashboard.invoiceCancelConfirm", { number: booking.fortnoxInvoiceNumber }),
      )
    ) {
      return;
    }
    try {
      await cancelInvoiceMutation.mutateAsync({
        documentNumber: booking.fortnoxInvoiceNumber,
        bookingId: booking.id,
        bookingDate: booking.date,
      });
    } catch (error) {
      alert(
        t("partnerPages.dashboard.invoiceCancelFailed", { message: error instanceof Error ? error.message : t("partnerPages.dashboard.unknownError") }),
      );
    }
  };

  const bookings = bookingsData?.data || [];

  // Sort bookings:
  // 1. Pending first (by createdAt ASC - oldest first)
  // 2. Then confirmed/invoiced (by trip date ASC - soonest first)
  // 3. Completed last
  const sortedBookings = useMemo(() => {
    if (sortMode === "date") return sortByDate(bookings);
    // Separate by status groups
    const pending = bookings.filter((b) => b.status === "pending");
    const active = bookings.filter(
      (b) => b.status === "confirmed" || b.status === "invoiced",
    );
    const completed = bookings.filter((b) => b.status === "completed");
    const cancelled = bookings.filter((b) => b.status === "cancelled");

    // Sort pending by createdAt ascending (oldest first = needs attention first)
    pending.sort((a, b) => {
      const dateA = a.createdAt || a.date;
      const dateB = b.createdAt || b.date;
      return new Date(dateA).getTime() - new Date(dateB).getTime();
    });

    // Sort confirmed/invoiced by trip date ascending (soonest first)
    active.sort((a, b) => {
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });

    // Sort completed by date descending (most recent first)
    completed.sort((a, b) => {
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });

    // Pending first, then active, then completed, then cancelled
    return [...pending, ...active, ...completed, ...cancelled];
  }, [bookings, sortMode]);

  // Filter bookings by status and search query
  const filteredBookings = useMemo(() => {
    let filtered = sortedBookings;

    // Apply status filter
    if (statusFilter === "active") {
      // Exclude only cancelled - completed is still actionable (needs invoicing)
      filtered = filtered.filter((b) => b.status !== "cancelled");
    } else if (statusFilter === "confirmed") {
      filtered = filtered.filter(
        (b) => b.status === "confirmed" && !hasTripStartPassed(b),
      );
    } else if (statusFilter === "toInvoice") {
      filtered = filtered.filter(
        (b) => b.status === "confirmed" && hasTripStartPassed(b),
      );
    } else if (statusFilter !== "all") {
      filtered = filtered.filter((b) => b.status === statusFilter);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((b) => {
        const customerName = b.customer?.name?.toLowerCase() || "";
        const userName = b.userName?.toLowerCase() || "";
        const confirmationCode = b.confirmationCode?.toLowerCase() || "";
        const partnerName = b.partnerName?.toLowerCase() || "";
        const bookingReference = b.bookingReference?.toLowerCase() || "";
        return (
          customerName.includes(query) ||
          userName.includes(query) ||
          confirmationCode.includes(query) ||
          partnerName.includes(query) ||
          bookingReference.includes(query)
        );
      });
    }

    return filtered;
  }, [sortedBookings, statusFilter, searchQuery]);

  // Calculate stats
  const stats = {
    pending: bookings.filter((b) => b.status === "pending").length,
    confirmed: bookings.filter(
      (b) => b.status === "confirmed" && !hasTripStartPassed(b),
    ).length,
    toInvoice: bookings.filter(
      (b) => b.status === "confirmed" && hasTripStartPassed(b),
    ).length,
  };

  // Pagination
  const totalPages = Math.ceil(filteredBookings.length / BOOKINGS_PER_PAGE);
  const startIndex = (currentPage - 1) * BOOKINGS_PER_PAGE;
  const endIndex = startIndex + BOOKINGS_PER_PAGE;
  const displayBookings = filteredBookings.slice(startIndex, endIndex);
  const pendingShown = displayBookings.filter((b) => b.status === "pending");
  const selectedIds = selected.filter((id) => pendingShown.some((b) => b.id === id));

  return (
    <div className="partner-surface">
      <PartnerPageHeader title={t("partnerPages.dashboard.title")} subtitle={t("partnerPages.dashboard.subtitle")} action={<Link to="/book" className="btn-primary min-h-11">{t("partnerPages.dashboard.create")}</Link>} />
      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-2 sm:gap-6 mb-6 sm:mb-8">
        <button type="button" aria-pressed={statusFilter === "pending"} onClick={() => setStatusFilter(statusFilter === "pending" ? "active" : "pending")} className={`card w-full text-left !p-3 sm:!p-6 ${statusFilter === "pending" ? "ring-2 ring-sea-500" : ""}`}>
          <div className="flex items-center">
            <div className="hidden sm:block p-3 bg-yellow-100 rounded-full">
              <svg
                className="w-6 h-6 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="sm:ml-4">
              <p className="text-sm font-medium text-gray-500">{t("partnerPages.dashboard.pending")}</p>
              <p className="text-2xl font-semibold text-yellow-600">
                {stats.pending}
              </p>
            </div>
          </div>
        </button>

        <button type="button" aria-pressed={statusFilter === "confirmed"} onClick={() => setStatusFilter(statusFilter === "confirmed" ? "active" : "confirmed")} className={`card w-full text-left !p-3 sm:!p-6 ${statusFilter === "confirmed" ? "ring-2 ring-sea-500" : ""}`}>
          <div className="flex items-center">
            <div className="hidden sm:block p-3 bg-green-100 rounded-full">
              <svg
                className="w-6 h-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div className="sm:ml-4">
              <p className="text-sm font-medium text-gray-500">{t("partnerPages.dashboard.confirmed")}</p>
              <p className="text-2xl font-semibold text-green-600">
                {stats.confirmed}
              </p>
            </div>
          </div>
        </button>

        <button type="button" aria-pressed={statusFilter === "toInvoice"} onClick={() => setStatusFilter(statusFilter === "toInvoice" ? "active" : "toInvoice")} className={`card w-full text-left !p-3 sm:!p-6 ${statusFilter === "toInvoice" ? "ring-2 ring-sea-500" : ""}`}>
          <div className="flex items-center">
            <div className="hidden sm:block p-3 bg-sea-100 rounded-full">
              <svg
                className="w-6 h-6 text-sea-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <div className="sm:ml-4">
              <p className="text-sm font-medium text-gray-500">{t("partnerPages.dashboard.toInvoice")}</p>
              <p className="text-2xl font-semibold text-sea-700">
                {stats.toInvoice}
              </p>
            </div>
          </div>
        </button>
      </div>

      {/* Recent Bookings */}
      <div className="mb-8">
        <div className="flex flex-col gap-4 mb-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-gray-900">{t("partnerPages.dashboard.bookings")}</h2>
            <button type="button" className="btn-secondary hidden text-sm sm:inline-flex" aria-pressed={!compactBookings} onClick={() => setCompactBookings(value => !value)}>
              {t("sitePolish.showBookingDetails")}
            </button>
          </div>
          <div className="grid grid-cols-1 items-center gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(12rem,1fr)_minmax(9rem,12rem)_minmax(8rem,10rem)_auto]">
            <div className="relative">
              <input
                type="text"
                aria-label={t("partnerPages.dashboard.searchLabel")}
                placeholder={t("partnerPages.dashboard.search")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input text-sm py-1.5 pl-9 pr-11 w-full"
              />
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              {searchQuery && (
                <button
                  aria-label={t("partnerPages.dashboard.clearSearch")}
                  onClick={() => setSearchQuery("")}
                  className="absolute right-0 top-1/2 flex min-h-11 min-w-11 -translate-y-1/2 items-center justify-center text-primary-600 hover:text-sea-900"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
            <select
              aria-label={t("partnerPages.dashboard.filterLabel")}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="input text-sm py-1.5"
            >
              <option value="active">{t("partnerPages.dashboard.active")}</option>
              <option value="all">{t("partnerPages.dashboard.all")}</option>
              <option value="pending">{t("partnerPages.dashboard.pending")}</option>
              <option value="confirmed">{t("partnerPages.dashboard.confirmed")}</option>
              <option value="toInvoice">{t("partnerPages.dashboard.toInvoice")}</option>
              <option value="invoiced">{t("partnerPages.dashboard.invoiced")}</option>
              <option value="completed">{t("partnerPages.dashboard.completed")}</option>
              <option value="cancelled">{t("partnerPages.dashboard.cancelled")}</option>
            </select>
            <select
              value={sortMode}
              onChange={(e) => update({ sort: e.target.value as BookingSort })}
              aria-label={t("common.sortBookings")}
              className="input text-sm py-1.5"
            >
              <option value="status">{t("common.sortStatus")}</option>
              <option value="date">{t("common.sortDate")}</option>
            </select>
            <Link
              to="/calendar"
              className="inline-flex min-h-11 items-center text-sea-700 hover:text-sea-900 text-sm font-medium whitespace-nowrap"
            >
              {t("partnerPages.dashboard.calendar")} →
            </Link>
          </div>
        </div>

        {bookingsLoading ? (
          <div className="flex items-center justify-center h-32" role="status">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sea-600"></div>
            <span className="sr-only">{t("common.loadingData")}</span>
          </div>
        ) : bookingsError ? (
          <QueryError onRetry={() => void refetchBookings()} />
        ) : displayBookings.length === 0 ? (
          <div className="card text-center py-8">
            <p className="text-gray-500">
              {searchQuery.trim()
                ? t("partnerPages.dashboard.noSearch", { query: searchQuery })
                : t("partnerPages.dashboard.noBookings")}
            </p>
            {!searchQuery.trim() && (
              <Link to="/book" className="btn-primary mt-4 inline-block">
                {t("partnerPages.dashboard.createFirst")}
              </Link>
            )}
            {searchQuery.trim() && (
              <button
                onClick={() => setSearchQuery("")}
                className="btn-secondary mt-4 inline-block"
              >
                {t("partnerPages.dashboard.clearSearch")}
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {isAdmin && pendingShown.length > 0 && (
              <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white p-3">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-5 w-5"
                    checked={selectedIds.length === pendingShown.length}
                    onChange={(e) => setSelected(e.target.checked ? pendingShown.map((b) => b.id) : [])}
                  />
                  {t("common.selectPending")} ({pendingShown.length})
                </label>
                <button type="button" className="btn btn-primary" disabled={selectedIds.length === 0 || bulkBusy} onClick={() => setBulkAsk(true)}>
                  {t("common.confirmSelected", { count: selectedIds.length })}
                </button>
              </div>
            )}
            {bulkResult && (
              <p role="status" className={`rounded-lg p-3 text-sm ${bulkResult.failed.length ? "bg-amber-50 text-amber-900" : "bg-green-50 text-green-900"}`}>
                {t("common.bulkResult", { done: bulkResult.done, total: bulkResult.total })}
                {bulkResult.failed.length > 0 && ` ${t("common.bulkFailed", { names: bulkResult.failed.join(", ") })}`}
              </p>
            )}
            {displayBookings.map((booking) => {
              // Partners can edit bookings from their partner, admins can edit all
              const canEdit = isAdmin || booking.partnerId === user?.partnerId;
              const card = (
                <BookingCard
                  compact={compactBookings}
                  key={booking.id}
                  booking={booking}
                  showActions={canEdit}
                  canCancel={canCancelBooking(booking, user)}
                  isAdmin={isAdmin}
                  onCancel={() => handleCancelBooking(booking)}
                  onViewDetails={() => setViewingBooking(booking)}
                  onEditDate={() => setEditingBooking(booking)}
                  onCreateInvoice={() => setInvoicingBooking(booking)}
                  onCancelInvoice={() => handleCancelInvoice(booking)}
                  onUpdateStatus={(status, invoiceNumber) =>
                    handleUpdateStatus(
                      booking.id,
                      booking.date,
                      status,
                      invoiceNumber,
                    )
                  }
                />
              );
              if (!isAdmin || booking.status !== "pending") return card;
              return (
                <div key={booking.id} className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-5 h-5 w-5 shrink-0"
                    aria-label={t("common.selectBooking", { name: booking.customer?.name })}
                    checked={selectedIds.includes(booking.id)}
                    onChange={(e) => setSelected((prev) => (e.target.checked ? [...prev, booking.id] : prev.filter((id) => id !== booking.id)))}
                  />
                  <div className="min-w-0 flex-1">{card}</div>
                </div>
              );
            })}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-gray-600">
                  {t("partnerPages.dashboard.showing", { start: startIndex + 1, end: Math.min(endIndex, filteredBookings.length), count: filteredBookings.length })}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="min-h-11 px-3 py-2 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("partnerPages.dashboard.previous")}
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => {
                        // Show first, last, current, and pages around current
                        if (
                          page === 1 ||
                          page === totalPages ||
                          Math.abs(page - currentPage) <= 1
                        ) {
                          return (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              aria-current={page === currentPage ? "page" : undefined}
                              aria-label={t("partnerPages.dashboard.page", { page })}
                              className={`min-h-11 min-w-11 px-3 py-2 text-sm rounded-lg ${
                                page === currentPage
                                  ? "bg-sea-600 text-white"
                                  : "border hover:bg-gray-50"
                              }`}
                            >
                              {page}
                            </button>
                          );
                        } else if (
                          page === currentPage - 2 ||
                          page === currentPage + 2
                        ) {
                          return (
                            <span key={page} className="px-1">
                              ...
                            </span>
                          );
                        }
                        return null;
                      },
                    )}
                  </div>
                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="min-h-11 px-3 py-2 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("partnerPages.dashboard.next")}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>



      {/* Edit Booking Modal */}
      {editingBooking && (
        <EditBookingModal
          booking={editingBooking}
          isAdmin={isAdmin}
          onClose={() => setEditingBooking(null)}
        />
      )}

      {/* Booking Details / History Modal */}
      {viewingBooking && (
        <BookingDetailsModal
          booking={viewingBooking}
          onClose={() => setViewingBooking(null)}
        />
      )}

      {cancelling && (
        <ConfirmDialog
          danger
          title={t("common.cancelBookingTitle")}
          confirmLabel={t(cancelling.stripePaymentStatus === "completed" ? "partnerPages.dashboard.cancelRefund" : "common.cancelBookingConfirm")}
          cancelLabel={t("common.keepBooking")}
          busy={cancelMutation.isPending}
          onCancel={() => { setCancelling(null); cancelMutation.reset(); }}
          onConfirm={() => cancelMutation.mutate({ id: cancelling.id, date: cancelling.date }, { onSuccess: () => setCancelling(null) })}
        >
          {cancelling.stripePaymentStatus === "completed" && <p className="mb-3">{t("partnerPages.dashboard.refundNotice")}</p>}
          {t("common.cancelBookingMessage", { guest: cancelling.customer?.name, date: cancelling.date, trip: cancelling.tripName })}
          {cancelMutation.isError && <p role="alert" className="mt-3 rounded-lg bg-red-50 p-3 text-red-800">{t(cancelMutation.error instanceof ApiError && cancelMutation.error.status === 403 ? "partnerPages.dashboard.cancelForbidden" : "partnerPages.dashboard.cancelFailed")}</p>}
        </ConfirmDialog>
      )}

      {bulkAsk && (
        <ConfirmDialog
          title={t("common.confirmSelectedTitle", { count: selectedIds.length })}
          confirmLabel={t("common.confirmAction")}
          cancelLabel={t("common.cancelAction")}
          busy={bulkBusy}
          onCancel={() => setBulkAsk(false)}
          onConfirm={() => void runBulkConfirm()}
        >
          {t("common.confirmSelectedText")}
        </ConfirmDialog>
      )}

      {/* Invoice Modal */}
      {invoicingBooking && (
        <InvoiceModal
          booking={invoicingBooking}
          onClose={() => {
            setInvoicingBooking(null);
            setInvoiceError(null);
          }}
          onCreateInvoice={handleCreateInvoice}
          isLoading={createInvoiceMutation.isPending}
          error={invoiceError}
        />
      )}
    </div>
  );
}
